/**
 * NowPlaying — the «Сейчас играет» screen: the record, the track, the slider and the buttons.
 *
 * Task 09. `spec.md` «Словарь»: «экран с пластинкой и кнопками управления». Opened from the
 * mini player (`components/MiniPlayer.tsx`) and drawn over the whole frame, tabs included:
 * the record is the product's one loud object, and it gets the screen to itself. «Свернуть»
 * brings the tabs back; the sound does not notice either way.
 *
 * ## It fits an iPhone SE without scrolling
 *
 * `spec.md` story 82. Every row has a height the stylesheet declares, and the record takes
 * whatever is left (`styles/nowplaying.css`): on a small phone the record is smaller, and
 * nothing is ever pushed below the edge. A problem line appearing shrinks the record, not the
 * buttons.
 *
 * ## The same pieces as the mini player
 *
 * The slider, the problem sentence and the words on the buttons are
 * `components/PlayerControls.tsx`, the mini player's own. The queue opens over this screen
 * from its header, the same sheet as from the mini player.
 *
 * ## The heart shares the title's row (task 11)
 *
 * Right of the title and the artist, inside `.nowMeta`: the row keeps its declared height, so
 * the screen still fits an iPhone SE, and the heart is where the eye already is.
 *
 * ## So does «Отправить в чат» (task 15)
 *
 * Left of the heart, for the same reason, and both are about this track. A share that failed
 * says so in the artist's line — the row already has one line that stands in for the artist,
 * «Загружается…» — until the next tap or the next track (`components/ShareButton.tsx`). It goes
 * before «Загружается…»: it answers the tap the listener has just made.
 *
 * ## The track is made ready for a scratch while the screen is open (task 18)
 *
 * `spec.md` «Пластинка», «Подготовка трека к скретчу»: only while this screen shows the track,
 * and not for a phone that asked for reduced motion — it has no gesture to scratch with. The
 * screen hands the track to `engine/scratch.ts` and takes it back when it closes or the track
 * changes; what may be downloaded and when is the engine's. «СКРЕТЧ» is lit exactly while the
 * engine says the track is ready.
 *
 * ## The deck and the status row under it (tasks 20–23)
 *
 * The stage holds the deck (`components/Deck.tsx`); under it a row of its own, with a declared
 * height like the others: a light, «ИГРАЕТ» or «ПАУЗА», the record (33 or 45), the tempo and
 * «СКРЕТЧ» — the mark lay over the foot of the stage in task 18 and moved here with the deck.
 *
 * ## «ПЕРЕХОД» while one track goes into the next (task 45)
 *
 * In the place of «СКРЕТЧ»: both decks sound, and the row says so (`spec.md` stories 105, 110).
 * Until task 57 it also said why a finger did not scratch then; since, the automat is the
 * crossfader's movement and a finger scratches through it (`engine/scratch.ts`), and the row says
 * only what plays. Reduced motion changes nothing here: an overlap is sound, not motion (story 111). A mix (task 47) says «МИКС» instead: the listener sees the next track was
 * brought to the tempo, and not laid over it — a blend is what a track without a sure measure gets.
 *
 * ## A loop in the status row (task 62)
 *
 * While the deck shown goes round a loop of the pult, «ПЕТЛЯ 4» — or «ДРОБЬ 1/8» while a ROLL pad is
 * held, story 183's own word for it — stands in the place of «СКРЕТЧ» and «ПЕРЕХОД»: the record
 * turns and the slider moves, yet the music comes back, and the row says why (`docs/superpowers/specs/2026-10-05-pult-design.md`,
 * task 62). The loop is the voice's (`store/scratch.ts`).
 *
 * ## The sleep timer in the status row (task 48)
 *
 * While the timer is on, «СОН 14:59» stands where «ИГРАЕТ» or «ПАУЗА» stood: the light before it
 * still says whether the sound plays, and the row has no room for both — on an iPhone SE of 320
 * points it was full already (`styles/nowplaying.css`).
 *
 * ## Shuffle and repeat in the row of buttons (task 44)
 *
 * перемешать · предыдущий · играть · следующий · повтор — the queue sheet's two modes, one tap
 * from the record instead of two (`spec.md` story 132). Each is a round button of 44, like the
 * others; the row keeps its height, so the screen still fits an iPhone SE. Lit in gold while on.
 *
 * ## The artist and the riddim are ways onward (task 50)
 *
 * The line under the title names the artist and, when the track has one, the riddim — each a
 * button that opens its list in «Треки» and folds this screen away (`spec.md` stories 142–143),
 * the shell's own way, as from the track card. The buttons take a finger's 44 points over a line
 * of 22: their extra height hangs into the gap above and below, so the row's declared height
 * (`--now-meta-h`) is what it was and the screen still fits an iPhone SE.
 *
 * ## «Пульт» in the header (task 54)
 *
 * Beside the queue: the pult (`screens/Pult.tsx`) opens over this screen and over everything, as
 * this screen opens over the tabs, and its «Свернуть» gives the turntable back. The sound does not
 * notice either way — the pult and the turntable are two views of the same decks.
 *
 * ## A bill, not a panel (task 41)
 *
 * Between the deck and the slider, the flag's two colours (`.flagStripe`), a row of its own
 * with a declared height; the rows stand on the ground, parted by hairlines, and the track's
 * name is large and gold (`styles/nowplaying.css`).
 *
 * ## «Под палец» — variant В (task 70)
 *
 * The owner's choice of 2026-10-24: the status row and the name come first, large, then the deck
 * across the whole width, the flag, the slider and the buttons — and under them «Дальше в
 * очереди», the next tracks of the queue with «Вся очередь ›». The screen never scrolls: that
 * block takes only the height the rest leaves (`flex: 1 1 0`), and draws as many whole rows as
 * it holds, none to three ({@link nextRowsThatFit}) — on an iPhone SE none at all, and then not
 * even its heading. A tap on one of them plays it, as in the queue sheet.
 */

import type { JSX } from 'react';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import {
  loadingText,
  NEXT_LABEL,
  NextIcon,
  PAUSE_LABEL,
  PLAY_LABEL,
  PREVIOUS_LABEL,
  PauseIcon,
  PlayIcon,
  PreviousIcon,
  ProblemLine,
  QUEUE_BUTTON_LABEL,
  QueueIcon,
  SeekBar,
  useHasNext,
  usePlaybackFlags,
} from '../components/PlayerControls';
import { FavoriteButton } from '../components/FavoriteButton';
import { QueueSheet, REPEAT_TEXT, SHUFFLE_LABEL } from '../components/QueueSheet';
import { SHARE_FAILED_TEXT, ShareButton } from '../components/ShareButton';
import { Cover } from '../components/Cover';
import { Deck } from '../components/Deck';
import { ROW_HEIGHT } from '../components/TrackRow';
import { useReducedMotion } from '../components/motion';
import { player } from '../engine/player';
import { telegram } from '../lib/telegram';
import { formatCountdown, formatDuration, formatPitch, trackByline } from '../lib/format';
import type { DeckSide } from '../lib/pult-layout';
import { beatsText } from '../lib/pult-pads';
import { scratch } from '../engine/scratch';
import { useDeck } from '../store/deck';
import type { Collection } from '../store/library';
import { usePlayer } from '../store/player';
import { playOrder, useQueue } from '../store/queue';
import type { RepeatMode } from '../store/queue';
import { useScratch } from '../store/scratch';
import '../styles/nowplaying.css';

import { Pult } from './Pult';

export const NOW_PLAYING_TITLE = 'Сейчас играет';
export const COLLAPSE_LABEL = 'Свернуть';
/** Task 18: lit while the track is ready to be scratched; in the status row since task 20. */
export const SCRATCH_MARK_TEXT = 'СКРЕТЧ';
/** Task 45: in its place while one track goes into the next. */
export const TRANSITION_MARK_TEXT = 'ПЕРЕХОД';
/** Task 47: in the same place, while the overlap is a mix. */
export const MIX_MARK_TEXT = 'МИКС';
/** Task 54: the header's way to the pult. */
export const PULT_OPEN_LABEL = 'Пульт';
/** Task 62: the status row's word for a loop, before its beats… */
export const LOOP_MARK_TEXT = 'ПЕТЛЯ';
/** …and for a ROLL pad held: story 183 calls it «дробь». */
export const ROLL_MARK_TEXT = 'ДРОБЬ';
/** Task 48: the status row's word for the sleep timer, before what it has left. */
export const SLEEP_TEXT = 'СОН';
/** Task 20: the status row's word while the sound is asked for. */
export const PLAYING_TEXT = 'ИГРАЕТ';
/** …and while it is not. */
export const PAUSED_TEXT = 'ПАУЗА';
/** Task 23: the status row's word before the tempo. */
export const TEMPO_TEXT = 'ТЕМП';

/** Task 70: the block under the buttons. */
export const NEXT_UP_TITLE = 'Дальше в очереди';
export const ALL_QUEUE_LABEL = 'Вся очередь ›';

/** Task 70: the most rows «Дальше в очереди» ever draws. */
export const NEXT_UP_MAX = 3;
/** Pixels: the block's heading, a finger's step tall for «Вся очередь ›» (`--hit`). */
export const NEXT_UP_HEAD = 44;

/**
 * Task 70: how many whole rows of the next tracks a block of `heightPx` holds — none to three,
 * none when even one row and the heading do not fit. The screen measures its block; the layout
 * test adds the declared heights up and asks the same question.
 */
export function nextRowsThatFit(heightPx: number): number {
  if (!Number.isFinite(heightPx)) return 0;
  return Math.max(0, Math.min(NEXT_UP_MAX, Math.floor((heightPx - NEXT_UP_HEAD) / ROW_HEIGHT)));
}

/** The positions of the queue that play after the current one in this pass, in that order. */
function upcomingPositions(
  queue: Parameters<typeof playOrder>[0] & { readonly index: number },
): number[] {
  const order = playOrder(queue);
  const at = order.indexOf(queue.index);
  return at === -1 ? [] : order.slice(at + 1, at + 1 + NEXT_UP_MAX);
}

function CollapseIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="nowIcon">
      <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

/** Task 54: the pult seen from above — two platters and the mixer between them. */
function PultIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="nowIcon">
      <rect
        x="1.5"
        y="5"
        width="21"
        height="14"
        rx="2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <circle cx="7" cy="12" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="17" cy="12" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M12 8v8" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function ShuffleIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="nowIcon">
      <path
        d="M3 7h4l10 10h4M3 17h4l3-3M14 10l3-3h4M18 4l3 3-3 3M18 14l3 3-3 3"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  );
}

function RepeatIcon({ mode }: { readonly mode: RepeatMode }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="nowIcon">
      <path
        d="M4 11V9a3 3 0 0 1 3-3h12M16 3l3 3-3 3M20 13v2a3 3 0 0 1-3 3H5M8 21l-3-3 3-3"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
      {mode === 'one' && (
        <path d="M11 10h1.5v5" fill="none" stroke="currentColor" strokeWidth="1.5" />
      )}
    </svg>
  );
}

/** Task 50: what the artist's name says it does. */
/** `then`, once deck `side` has no hand on its record — now, or at the lift (task 57). */
function afterTheHand(side: DeckSide, then: () => void): void {
  if (useDeck.getState().decks[side].hand !== 'record') {
    then();
    return;
  }
  const stop = useDeck.subscribe((state) => {
    if (state.decks[side].hand === 'record') return;
    stop();
    then();
  });
}

export function artistLinkLabel(performer: string): string {
  return `Все треки: ${performer}`;
}

/** Task 50: what the riddim's name says it does. */
export function riddimLinkLabel(riddim: string): string {
  return `Все треки на riddim ${riddim}`;
}

export interface NowPlayingProps {
  readonly onClose: () => void;
  /**
   * Task 50: opens an artist's or a riddim's list in «Треки» — the shell's own way, as from the
   * card. Absent, the names are not buttons (a screen drawn on its own, in a test).
   */
  readonly onOpenCollection?: ((collection: Collection) => void) | undefined;
}

export function NowPlaying({ onClose, onOpenCollection }: NowPlayingProps): JSX.Element | null {
  const titleId = useId();
  const collapseRef = useRef<HTMLButtonElement | null>(null);
  const [queueOpen, setQueueOpen] = useState(false);
  const closeQueue = useCallback(() => {
    setQueueOpen(false);
  }, []);
  const [pultOpen, setPultOpen] = useState(false);
  const closePult = useCallback(() => {
    setPultOpen(false);
  }, []);
  /** The track whose share failed: the line goes with the next track by itself. */
  const [shareFailedFor, setShareFailedFor] = useState<number | undefined>(undefined);
  const track = usePlayer((s) => s.track);
  const resumeFromS = usePlayer((s) => s.resumeFromS);
  const status = usePlayer((s) => s.status);
  const buffering = usePlayer((s) => s.buffering);
  const crossfading = usePlayer((s) => s.crossfading);
  const mixing = usePlayer((s) => s.mixing);
  const sleepAt = usePlayer((s) => s.sleepAt);
  const sleepLeftS = usePlayer((s) => s.sleepLeftS);
  const hasNext = useHasNext();
  const { playing, loading } = usePlaybackFlags();
  const reducedMotion = useReducedMotion();
  // Task 57: the deck this screen shows — the active one; its own readiness and its own voice.
  const activeSide = useDeck((s) => s.active);
  const scratchReady = useScratch((s) => s.byDeck[activeSide]);
  const loop = useScratch((s) => s.loops[activeSide]);
  const recordSize = useDeck((s) => s.size);
  const pitch = useDeck((s) => s.pitch);
  const shuffle = useQueue((s) => s.shuffle);
  const repeat = useQueue((s) => s.repeat);
  const entries = useQueue((s) => s.entries);
  // A string, so a queue that moves without changing what comes next re-renders nothing here.
  const upcomingKey = useQueue((s) => upcomingPositions(s).join(','));
  const upcoming = upcomingKey === '' ? [] : upcomingKey.split(',').map(Number);

  /** Task 70: how many rows of «Дальше в очереди» the screen has room for. */
  const nextRef = useRef<HTMLElement | null>(null);
  const [nextFit, setNextFit] = useState(NEXT_UP_MAX);
  useLayoutEffect(() => {
    const block = nextRef.current;
    // Where nothing is laid out — a test — there is nothing to measure: all three rows stand.
    if (block === null || typeof ResizeObserver !== 'function') return undefined;
    const measure = (): void => {
      setNextFit(nextRowsThatFit(block.getBoundingClientRect().height));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(block);
    return () => {
      observer.disconnect();
    };
  }, []);

  /** Task 50: the list opens in «Треки», and this screen gets out of its way. */
  const openFromHere = (collection: Collection): void => {
    onOpenCollection?.(collection);
    onClose();
  };

  useEffect(() => {
    if (track === undefined || reducedMotion) return undefined;
    const release = scratch().hold(track, activeSide);
    // Task 57: the turntable turns to the other deck at the middle of a transition — under a finger
    // the touch is still this deck's, and its track is let go at the lift, not under the hand.
    return () => {
      afterTheHand(activeSide, release);
    };
  }, [track, reducedMotion, activeSide]);

  useEffect(() => {
    collapseRef.current?.focus();
  }, []);

  useEffect(() => {
    // The queue sheet and the pult over this screen close on their own Escape; this one waits.
    if (queueOpen || pultOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose, queueOpen, pultOpen]);

  if (track === undefined) return null;
  // The record turns while sound comes out, and only then: a record that turns over silence
  // is lying about what the listener hears.
  const spinning = status === 'playing' && !buffering;

  return (
    <>
      <section className="now" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="nowHead">
          <button
            ref={collapseRef}
            type="button"
            className="nowHeadButton"
            aria-label={COLLAPSE_LABEL}
            onClick={onClose}
          >
            <CollapseIcon />
          </button>
          <h2 id={titleId} className="nowHeading">
            {NOW_PLAYING_TITLE}
          </h2>
          <button
            type="button"
            className="nowHeadButton"
            aria-label={QUEUE_BUTTON_LABEL}
            aria-haspopup="dialog"
            onClick={() => {
              setQueueOpen(true);
            }}
          >
            <QueueIcon className="nowIcon" />
          </button>
          <button
            type="button"
            className="nowHeadButton"
            aria-label={PULT_OPEN_LABEL}
            aria-haspopup="dialog"
            onClick={() => {
              setPultOpen(true);
            }}
          >
            <PultIcon />
          </button>
        </header>

        <div className="nowStatus">
          <span className="statusLight" data-on={playing ? 'true' : 'false'} aria-hidden="true" />
          <span>
            {sleepAt !== undefined
              ? `${SLEEP_TEXT}${sleepLeftS === undefined ? '' : ` ${formatCountdown(sleepLeftS)}`}`
              : playing
                ? PLAYING_TEXT
                : PAUSED_TEXT}
          </span>
          <span aria-hidden="true">·</span>
          <span>{recordSize}</span>
          <span aria-hidden="true">·</span>
          <span>
            {TEMPO_TEXT} {formatPitch(pitch)}
          </span>
          {loop !== undefined ? (
            <>
              <span aria-hidden="true">·</span>
              <span className="scratchMark">
                {`${loop.roll ? ROLL_MARK_TEXT : LOOP_MARK_TEXT} ${beatsText(loop.beats)}`}
              </span>
            </>
          ) : crossfading ? (
            <>
              <span aria-hidden="true">·</span>
              <span className="scratchMark">{mixing ? MIX_MARK_TEXT : TRANSITION_MARK_TEXT}</span>
            </>
          ) : (
            scratchReady && (
              <>
                <span aria-hidden="true">·</span>
                <span className="scratchMark">{SCRATCH_MARK_TEXT}</span>
              </>
            )
          )}
        </div>

        <div className="nowMeta">
          <div className="nowText">
            <p className="nowTitle">{track.title}</p>
            {shareFailedFor === track.id ? (
              <p className="nowByline" role="status">
                {SHARE_FAILED_TEXT}
              </p>
            ) : loading ? (
              <p className="nowByline" role="status">
                {loadingText(resumeFromS)}
              </p>
            ) : onOpenCollection === undefined ? (
              <p className="nowByline">{track.performer}</p>
            ) : (
              <p className="nowByline nowLinks">
                {track.performer !== '' && track.artist !== null && (
                  <button
                    type="button"
                    className="nowLink"
                    aria-label={artistLinkLabel(track.performer)}
                    onClick={() => {
                      openFromHere({
                        kind: 'artist',
                        key: track.artist?.key ?? '',
                        name: track.artist?.name ?? '',
                      });
                    }}
                  >
                    {track.performer}
                  </button>
                )}
                {/* A performer the catalogue has no list for is still named, as before task 50. */}
                {track.performer !== '' && track.artist === null && <span>{track.performer}</span>}
                {track.riddim !== null && (
                  <>
                    {track.performer !== '' && (
                      <span className="nowLinkSep" aria-hidden="true">
                        ·
                      </span>
                    )}
                    <button
                      type="button"
                      className="nowLink nowRiddim"
                      aria-label={riddimLinkLabel(track.riddim.name)}
                      onClick={() => {
                        if (track.riddim !== null)
                          openFromHere({ kind: 'riddim', ...track.riddim });
                      }}
                    >
                      {track.riddim.name}
                    </button>
                  </>
                )}
              </p>
            )}
          </div>
          <ShareButton
            trackId={track.id}
            onFailedChange={(failed) => {
              setShareFailedFor(failed ? track.id : undefined);
            }}
            className="nowButton"
            iconClassName="nowIcon"
          />
          <FavoriteButton
            trackId={track.id}
            title={track.title}
            className="nowButton nowFav"
            iconClassName="nowIcon"
          />
        </div>

        <div className="nowStage">
          <Deck
            cover={track.cover}
            title={track.title}
            performer={track.performer}
            spinning={spinning}
          />
        </div>

        <div className="flagStripe" aria-hidden="true" />

        <SeekBar />

        <div className="nowControls">
          <button
            type="button"
            className="nowButton"
            aria-label={SHUFFLE_LABEL}
            aria-pressed={shuffle}
            onClick={() => {
              useQueue.getState().toggleShuffle();
            }}
          >
            <ShuffleIcon />
          </button>
          <button
            type="button"
            className="nowButton"
            aria-label={PREVIOUS_LABEL}
            onClick={() => {
              player().previous();
            }}
          >
            <PreviousIcon className="nowIcon" />
          </button>
          <button
            type="button"
            className={loading ? 'nowPlay nowPlayBusy' : 'nowPlay'}
            aria-label={playing ? PAUSE_LABEL : PLAY_LABEL}
            onClick={() => {
              // Task 50: a light knock, like the deck's keys.
              telegram.haptic('impact');
              player().toggle();
            }}
          >
            {playing ? <PauseIcon className="nowPlayIcon" /> : <PlayIcon className="nowPlayIcon" />}
          </button>
          <button
            type="button"
            className="nowButton"
            aria-label={NEXT_LABEL}
            disabled={!hasNext}
            onClick={() => {
              player().next();
            }}
          >
            <NextIcon className="nowIcon" />
          </button>
          <button
            type="button"
            className="nowButton"
            aria-label={REPEAT_TEXT[repeat]}
            aria-pressed={repeat !== 'off'}
            onClick={() => {
              useQueue.getState().cycleRepeat();
            }}
          >
            <RepeatIcon mode={repeat} />
          </button>
        </div>

        <ProblemLine />

        <section ref={nextRef} className="nowNext" aria-label={NEXT_UP_TITLE}>
          {nextFit > 0 && upcoming.length > 0 && (
            <>
              <div className="nowNextHead">
                <h3 className="nowNextTitle">{NEXT_UP_TITLE}</h3>
                <button
                  type="button"
                  className="linkButton"
                  aria-haspopup="dialog"
                  onClick={() => {
                    setQueueOpen(true);
                  }}
                >
                  {ALL_QUEUE_LABEL}
                </button>
              </div>
              <ul className="nowNextList">
                {upcoming.slice(0, nextFit).map((position) => {
                  const next = entries[position];
                  if (next === undefined) return null;
                  const byline = trackByline(next.track.performer, next.track.year);
                  const duration = formatDuration(next.track.durationS);
                  return (
                    <li key={next.key} className="row trackRow">
                      <button
                        type="button"
                        className="rowButton"
                        onClick={() => {
                          player().playAt(position);
                        }}
                      >
                        <Cover src={next.track.cover} className="trackCover" />
                        <span className="trackText">
                          <span className="trackTitle">{next.track.title}</span>
                          {byline !== '' && <span className="trackByline">{byline}</span>}
                        </span>
                        {duration !== '' && <span className="trackDuration">{duration}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </section>
      </section>
      {queueOpen && <QueueSheet onClose={closeQueue} />}
      {pultOpen && <Pult onClose={closePult} />}
    </>
  );
}
